import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import subjectService from "../../services/subjectService";

// Async thunks
export const fetchSubjects = createAsyncThunk(
  "subjects/fetchSubjects",
  async (params, { rejectWithValue }) => {
    try {
      const response = await subjectService.getAllSubjects(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subjects"
      );
    }
  }
);

export const createSubject = createAsyncThunk(
  "subjects/createSubject",
  async (subjectData, { rejectWithValue }) => {
    try {
      const response = await subjectService.createSubject(subjectData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create subject"
      );
    }
  }
);

export const updateSubject = createAsyncThunk(
  "subjects/updateSubject",
  async ({ id, subjectData }, { rejectWithValue }) => {
    try {
      const response = await subjectService.updateSubject(id, subjectData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update subject"
      );
    }
  }
);

export const deleteSubject = createAsyncThunk(
  "subjects/deleteSubject",
  async (id, { rejectWithValue }) => {
    try {
      const response = await subjectService.deleteSubject(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete subject"
      );
    }
  }
);

const subjectSlice = createSlice({
  name: "subjects",
  initialState: {
    subjects: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subjects
      .addCase(fetchSubjects.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubjects.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.subjects = responseData.subjects || responseData || [];
      })
      .addCase(fetchSubjects.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create subject
      .addCase(createSubject.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createSubject.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newSubject = responseData.subject || responseData;
        if (newSubject) {
          state.subjects.unshift(newSubject);
        }
        state.success = action.payload.message || "Subject created successfully";
      })
      .addCase(createSubject.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      // Update subject
      .addCase(updateSubject.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateSubject.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedSubject = responseData.subject || responseData;
        const index = state.subjects.findIndex((s) => s.id === updatedSubject.id);
        if (index !== -1) {
          state.subjects[index] = updatedSubject;
        }
        state.success = action.payload.message || "Subject updated successfully";
      })
      .addCase(updateSubject.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      // Delete subject
      .addCase(deleteSubject.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteSubject.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.subjects = state.subjects.filter((s) => s.id !== action.payload.id);
        state.success = action.payload.response.message || "Subject deleted successfully";
      })
      .addCase(deleteSubject.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = subjectSlice.actions;
export default subjectSlice.reducer;
